import { PanelContext } from "./PanelContext";
import { PanelHeader } from "./PanelHeader";
import { PanelContent } from "./PanelContent";
import { PanelMobile } from "./PanelMobile";
import { PanelContainer, PanelInner } from "./styles";
import { usePanelShell } from "./PanelShared";
import type { TypePanelProps } from "./PanelTypes";

/**
 * Styled-components shell — the backward-compatible fallback. The hybrid router
 * sends consumers here whenever they pass a `COMMON`-mixin styled-system prop
 * (e.g. `mt`, `mb`, `mr`) or a `styled()` extension, so the full styled prop
 * surface on the outer container keeps working exactly as before.
 */
export const PanelStyled = (props: TypePanelProps) => {
  const shell = usePanelShell(props);
  const {
    rest,
    header,
    headerless,
    footer,
    title,
    titleId,
    direction,
    width,
    gap,
    id,
    ariaLabel,
    isPanelOpen,
    isMobile,
    enrichedValue,
    renderedChildren,
    effectiveAriaLabelledBy,
  } = shell;

  if (isMobile) {
    return <PanelMobile shell={shell} />;
  }

  return (
    <PanelContext.Provider value={enrichedValue}>
      <PanelContainer
        {...rest}
        $isOpen={isPanelOpen}
        $direction={direction}
        $width={width}
        $gap={gap}
        data-qa-panel={id}
        data-qa-panel-isopen={isPanelOpen}
        aria-labelledby={effectiveAriaLabelledBy}
        aria-label={ariaLabel}
      >
        <PanelInner $direction={direction} $width={width}>
          {header ??
            (headerless ? null : <PanelHeader title={title} id={titleId} />)}
          <PanelContent>{renderedChildren}</PanelContent>
          {footer}
        </PanelInner>
      </PanelContainer>
    </PanelContext.Provider>
  );
};
